import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, MessageSquare, AlertTriangle, FileText,
  Briefcase, Coffee, Receipt, Package, Award, GraduationCap, Calendar,
  ArrowUp, Eye, Send, ShieldAlert, TrendingUp, BarChart3,
} from "lucide-react";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, StatusBadge, SectionCard, PillTabs, EmptyState,
} from "@/components/app/PageKit";

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

const CATEGORIES = [
  { key: "leave",         label: "Leave",         icon: Coffee,         tone: "bg-cyan-50 text-cyan-700"      },
  { key: "attendance",    label: "Attendance",    icon: Clock,          tone: "bg-blue-50 text-blue-700"      },
  { key: "timesheet",     label: "Timesheet",     icon: Calendar,       tone: "bg-violet-50 text-violet-700"  },
  { key: "expense",       label: "Expense",       icon: Receipt,        tone: "bg-emerald-50 text-emerald-700"},
  { key: "asset",         label: "Asset",         icon: Package,        tone: "bg-amber-50 text-amber-700"    },
  { key: "document",      label: "Document",      icon: FileText,       tone: "bg-rose-50 text-rose-700"      },
  { key: "recruitment",   label: "Recruitment",   icon: Briefcase,      tone: "bg-fuchsia-50 text-fuchsia-700"},
  { key: "performance",   label: "Performance",   icon: Award,          tone: "bg-orange-50 text-orange-700"  },
];

export default function Approvals() {
  const [pending, setPending] = useState([]);
  const [summary, setSummary] = useState({ pending_for_me: 0, my_pending: 0 });
  const [tab, setTab] = useState("review");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/leaves", { params: { scope: "pending-me" } }),
      api.get("/approvals/summary"),
    ]);
    setPending(p.data); setSummary(s.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/leaves/${id}/action`, { action });
      toast.success(`Request ${action}d`);
      load();
    } catch { toast.error("Failed"); }
  };

  // Filter
  const filtered = pending.filter((l) => {
    if (search && !`${l.employee?.full_name || ""} ${l.reason || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (catFilter && catFilter !== "leave") return false; // backend only has leaves for now
    return true;
  });

  // Recent decisions (mock—wire to backend later)
  const recentDecisions = [
    { id: "d1", who: "Riya Malhotra",  type: "Leave",     action: "approved", at: "2h ago" },
    { id: "d2", who: "Vikram Singh",   type: "Expense",   action: "approved", at: "5h ago" },
    { id: "d3", who: "Karan Joshi",    type: "Timesheet", action: "rejected", at: "1d ago" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="approvals-page">
      <PageHeader
        category="APPROVAL CENTER"
        title="Approvals"
        description="Review, approve, and track every workflow that needs your sign-off — leaves, expenses, timesheets, assets, and more."
        primaryAction={{ label: "Approve All Eligible", icon: CheckCircle2, onClick: () => toast.info("Bulk approve eligible items") }}
        secondaryAction={{ label: "Decision Log", icon: Eye, onClick: () => toast.info("Open decision history") }}
      />

      {/* KPIs */}
      <KPIGrid cols={5}>
        <KPICard icon={Clock}        tone="amber"   label="Pending For Me"    value={summary.pending_for_me} sub={summary.pending_for_me ? "Action needed" : "All caught up"} testid="ap-kpi-mine" />
        <KPICard icon={Send}         tone="blue"    label="My Pending"        value={summary.my_pending}    sub="Awaiting others"           testid="ap-kpi-my-pending" />
        <KPICard icon={TrendingUp}   tone="emerald" label="SLA Health"        value="On Track"              sub="0 breaches this week"      testid="ap-kpi-sla" />
        <KPICard icon={ShieldAlert}  tone="rose"    label="Escalated"         value={0}                     sub="No urgent items"           testid="ap-kpi-escalated" />
        <KPICard icon={CheckCircle2} tone="violet"  label="Approved (7d)"     value={recentDecisions.filter(d => d.action === "approved").length} sub="Last 7 days" testid="ap-kpi-approved" />
      </KPIGrid>

      {/* Tabs */}
      <PillTabs
        value={tab} onChange={setTab}
        tabs={[
          { value: "review",     label: "Review",    badge: pending.length },
          { value: "monitor",    label: "Monitor"  },
          { value: "track",      label: "Track"    },
          { value: "escalated",  label: "Escalated", badge: 0 },
        ]}
        testid="ap-tabs"
      />

      <FilterBar
        search={search} onSearch={setSearch}
        searchPlaceholder="Search by employee, request type, reason…"
        filters={[
          { label: "All Categories",  value: catFilter, onChange: setCatFilter, options: CATEGORIES.map(c => ({ value: c.key, label: c.label })) },
          { label: "All Priorities",  onChange: () => {}, options: ["High","Medium","Low"] },
          { label: "All SLAs",        onChange: () => {}, options: ["Healthy","At-Risk","Breached"] },
        ]}
      />

      {/* Main + right rail */}
      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0 space-y-5">
          {tab === "review" && (
            <SectionCard icon={CheckCircle2} title="Pending for Your Decision" sub={`${filtered.length} item(s)`}>
              {filtered.length === 0 ? (
                <EmptyState
                  icon={CheckCircle2} title="All caught up!"
                  message="Nothing awaiting your approval right now. Browse Track to see your decision history or Monitor for SLA insights."
                  action={{ label: "View Decision Log", icon: Eye, onClick: () => setTab("track") }}
                />
              ) : (
                <div className="space-y-2">
                  {filtered.map((l) => {
                    const cat = CATEGORIES.find(c => c.key === "leave");
                    const days = l.days || 1;
                    const sla = days <= 1 ? { label: "Due in 4h", tone: "text-amber-700 bg-amber-50" } : { label: "Due in 1d", tone: "text-emerald-700 bg-emerald-50" };
                    return (
                      <div key={l.id} className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/30 transition" data-testid={`approval-${l.id}`}>
                        <Avatar className="w-10 h-10 ring-2 ring-white shadow-sm shrink-0">
                          {l.employee?.avatar_url && <AvatarImage src={l.employee.avatar_url} alt={l.employee.full_name} />}
                          <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(l.employee?.full_name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[14px] font-bold text-slate-900 truncate">{l.employee?.full_name || "Employee"}</span>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${cat.tone}`}>
                              <cat.icon className="w-2.5 h-2.5" /> {cat.label}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${sla.tone}`}>{sla.label}</span>
                          </div>
                          <div className="text-[12px] text-slate-600 mt-1">
                            <span className="font-semibold capitalize">{l.leave_type}</span> · {days} day{days > 1 ? "s" : ""} · {new Date(l.start_date).toLocaleDateString([], { month: "short", day: "numeric" })} → {new Date(l.end_date).toLocaleDateString([], { month: "short", day: "numeric" })}
                          </div>
                          {l.reason && <div className="text-[12px] text-slate-700 mt-1 italic">"{l.reason}"</div>}
                        </div>
                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 shrink-0">
                          <Button size="sm" onClick={() => act(l.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-[11px]" data-testid={`approve-${l.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => act(l.id, "reject")} className="border-rose-200 text-rose-700 hover:bg-rose-50 h-8 px-3 text-[11px]" data-testid={`reject-${l.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-200 h-8 px-2 text-[11px]">
                            <MessageSquare className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          )}
          {tab === "monitor" && (
            <SectionCard icon={BarChart3} title="SLA Monitor" sub="Workflow health across approval categories">
              <div className="space-y-3">
                {CATEGORIES.slice(0, 6).map((c, i) => {
                  const pct = [92, 88, 95, 78, 100, 84][i];
                  return (
                    <div key={c.key}>
                      <div className="flex items-center justify-between text-[12px] mb-1">
                        <span className="inline-flex items-center gap-2 font-semibold text-slate-700"><c.icon className="w-3.5 h-3.5" />{c.label}</span>
                        <span className={`tabular-nums font-bold ${pct >= 90 ? "text-emerald-700" : pct >= 80 ? "text-amber-700" : "text-rose-700"}`}>{pct}% on-time</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${pct >= 90 ? "bg-emerald-500" : pct >= 80 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}
          {tab === "track" && (
            <SectionCard icon={Eye} title="Decision Log" sub="Your recent approval history">
              <div className="space-y-2">
                {recentDecisions.map(d => (
                  <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                    <Avatar className="w-9 h-9 ring-2 ring-white"><AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(d.who)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{d.who} — {d.type}</div>
                      <div className="text-[11px] text-slate-500">{d.at}</div>
                    </div>
                    <StatusBadge status={d.action} />
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
          {tab === "escalated" && (
            <EmptyState icon={ShieldAlert} title="No escalated items" message="Items breach SLA when they remain pending beyond category thresholds. Healthy queue!" />
          )}
        </div>

        {/* Right Rail */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={Briefcase} title="Approval Categories" sub="Quick navigation">
            <div className="grid grid-cols-2 gap-2">
              {CATEGORIES.map(c => (
                <button key={c.key} onClick={() => setCatFilter(c.key)} className={`flex items-center gap-2 p-2.5 rounded-lg ${c.tone} hover:scale-[1.02] transition-transform`} data-testid={`cat-${c.key}`}>
                  <c.icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="text-[11px] font-bold truncate">{c.label}</span>
                </button>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={Eye} title="Recent Decisions" sub="Your last 3 actions">
            <div className="space-y-2.5">
              {recentDecisions.map(d => (
                <div key={d.id} className="flex items-center gap-2.5">
                  <Avatar className="w-8 h-8 ring-2 ring-white"><AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(d.who)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-slate-900 truncate">{d.who}</div>
                    <div className="text-[10px] text-slate-500">{d.type} · {d.at}</div>
                  </div>
                  {d.action === "approved" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-rose-500" />}
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={AlertTriangle} title="Escalation Alerts" sub="Watch list">
            <div className="text-[12px] text-emerald-700 bg-emerald-50 p-2.5 rounded-lg font-semibold inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> No active escalations
            </div>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}
